import type { FC } from 'react';
import React, { useState, useCallback } from 'react';
import type { WithIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { styled } from '@compiled/react';

import EditorRemoveIcon from '@atlaskit/icon/core/delete';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { PRIMARY_COLORS } from '../colors.ts';

import type { RemoveButtonProps } from './types.ts';
import { i18n } from './i18n.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RemoveButtonWrapper = styled.button({
	width: '24px',
	height: '24px',
	border: 'none',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	cursor: 'pointer',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	outlineColor: `${PRIMARY_COLORS.RED}`,
	backgroundColor: 'transparent',
	color: token('color.text'),
	'&:hover': {
		backgroundColor: token('color.background.neutral'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		color: `${token('color.text.danger')} !important`,
	},
	'&:active': {
		backgroundColor: token('color.background.neutral'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		color: `${token('color.text.danger')} !important`,
	},
	'&:focus': {
		backgroundColor: token('color.background.neutral'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		color: `${token('color.text.danger')} !important`,
	},
	'&:disabled': {
		cursor: 'not-allowed',
		opacity: 0.7,
		color: token('color.text.disabled'),
		backgroundColor: 'transparent',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RemoveButtonPlaceholder = styled.div({
	width: '24px',
	height: '24px',
});

const RemoveButtonComponent: FC<RemoveButtonProps> = ({
	onClick,
	onMouseEnter,
	onMouseLeave,
	hasContentState,
	userOffline,
	intl: { formatMessage },
}) => {
	const [isFocused, setIsFocused] = useState<boolean>(false);

	const handleOnFocus = useCallback(() => {
		setIsFocused(true);
		onMouseEnter();
	}, [onMouseEnter]);

	const handleOnBlur = useCallback(() => {
		setIsFocused(false);
		onMouseLeave();
	}, [onMouseLeave]);

	const handleOnMouseLeave = useCallback(() => {
		// only call onMouseLeave if we're not focused because we want to keep the buttons red if we are
		if (!isFocused) {
			onMouseLeave();
		}
	}, [isFocused, onMouseLeave]);

	return hasContentState ? (
		<Tooltip content={formatMessage(i18n.remove)} position="top">
			<RemoveButtonWrapper
				onClick={onClick}
				data-testid="remove-button"
				onMouseEnter={onMouseEnter}
				onMouseLeave={handleOnMouseLeave}
				onFocus={handleOnFocus}
				onBlur={handleOnBlur}
				disabled={userOffline}
				aria-label={formatMessage(i18n.removeButtonAriaLabel)}
			>
				<EditorRemoveIcon color="currentColor" spacing="spacious" label="" />
			</RemoveButtonWrapper>
		</Tooltip>
	) : (
		<RemoveButtonPlaceholder data-testid="remove-button-placeholder" />
	);
};

export const RemoveButton: FC<WithIntlProps<RemoveButtonProps>> & {
	WrappedComponent: React.ComponentType<RemoveButtonProps>;
} = injectIntl(RemoveButtonComponent);
